import React from 'react';
import { ZodiacSign, signImageUrl } from '../data/zodiacSigns';
import { CompatibilityCardData } from '../services/compatibilityApi';

interface CompatibilityCardProps {
  signA: ZodiacSign;
  signB: ZodiacSign;
  data: CompatibilityCardData;
  /** Ref pro elemento raiz — alvo do html-to-image na página que usa este card. */
  cardRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Tamanho nativo do card = formato Story (1080×1920). Todas as medidas internas
 * são em px sobre essa base, então a captura (html-to-image) sai exata sem
 * depender de container queries — que não resolvem de forma confiável no clone
 * do html-to-image, principalmente no mobile. Na tela o card é reduzido com
 * `transform: scale()` pelo wrapper em CompatibilidadePage.
 */
export const CARD_W = 1080;
export const CARD_H = 1920;

const METRICS: { key: keyof CompatibilityCardData; label: string; main?: boolean }[] = [
  { key: 'family_pct', label: 'Família' },
  { key: 'friendship_pct', label: 'Amizade' },
  { key: 'overall_pct', label: 'Geral', main: true },
  { key: 'sex_pct', label: 'Sexo' },
  { key: 'love_pct', label: 'Amor' },
];

/** Estrela de 4 pontas desenhada — não depende de glifo de fonte (o caractere ✦
 *  não existe em fontes serif e caía num fallback com métricas ruins na captura). */
const Sparkle: React.FC = () => (
  <svg className="cc-spark" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0c1 6 5 10 12 12-7 2-11 6-12 12-1-6-5-10-12-12C7 10 11 6 12 0Z" />
  </svg>
);

const Ring: React.FC<{ value: number; label: string; main?: boolean }> = ({ value, label, main }) => {
  const r = 22;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const off = c * (1 - clamped / 100);
  return (
    <div className={`cc-ring${main ? ' cc-ring--main' : ''}`}>
      <svg viewBox="0 0 52 52" role="img" aria-label={`${label} ${clamped} por cento`}>
        <circle className="cc-track" cx="26" cy="26" r={r} fill="none" strokeWidth="5" />
        <circle
          className="cc-fill" cx="26" cy="26" r={r} fill="none" strokeWidth="5"
          strokeDasharray={c} strokeDashoffset={off}
        />
        <text className="cc-pct" x="26" y="26" dominantBaseline="central" fontSize="15">{clamped}</text>
      </svg>
      <span className="cc-ring-lbl">{label}</span>
    </div>
  );
};

const CompatibilityCard: React.FC<CompatibilityCardProps> = ({ signA, signB, data, cardRef }) => {
  const strengths = (data.strengths || []).slice(0, 5);
  const weaknesses = (data.weaknesses || []).slice(0, 5);

  return (
    <div ref={cardRef} className="cc-card">
      <style>{CARD_CSS}</style>
      <div className="cc-inner">

        <div className="cc-top">
          <p className="cc-title">Compatibilidade</p>
          <div className="cc-signs">
            <div className="cc-sign">
              <img src={signImageUrl(signA)} alt={signA.name} />
              <b>{signA.name}</b>
            </div>
            <span className="cc-amp">+</span>
            <div className="cc-sign">
              <img src={signImageUrl(signB)} alt={signB.name} />
              <b>{signB.name}</b>
            </div>
          </div>
        </div>

        <div className="cc-rings">
          {METRICS.map(m => (
            <Ring key={m.key as string} value={Number(data[m.key]) || 0} label={m.label} main={m.main} />
          ))}
        </div>

        <div className="cc-verdict">
          <div className="cc-col cc-col--pos">
            <h4>Pontos fortes</h4>
            <ul>{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <div className="cc-col cc-col--neg">
            <h4>Pontos fracos</h4>
            <ul>{weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        </div>

        {data.advice && (
          <div className="cc-advice">
            <p className="cc-advice-k">Conselho</p>
            <p>{data.advice}</p>
          </div>
        )}

        <div className="cc-foot">
          <span className="cc-brand">
            <Sparkle /> Astralyz <Sparkle />
          </span>
          <span className="cc-cta">descubra a sua em astralyz.com.br</span>
        </div>

      </div>
    </div>
  );
};

const CARD_CSS = `
.cc-card {
  width: ${CARD_W}px;
  height: ${CARD_H}px;
  border-radius: 32px;
  overflow: hidden;
  background: linear-gradient(168deg, #1e1332 0%, #2d1b4e 52%, #17102a 100%);
  color: #f5f2f8;
  box-shadow: 0 24px 60px -22px rgba(20, 8, 40, 0.5);
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
}
.cc-inner {
  width: 100%; height: 100%;
  padding: 59px 76px 65px;
  display: flex; flex-direction: column; align-items: center; justify-content: space-between;
  background: radial-gradient(72% 34% at 50% 4%, rgba(227, 189, 74, 0.15), transparent 70%);
}
.cc-top { width: 100%; }
.cc-title {
  margin: 0; text-align: center;
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif; font-weight: 700;
  font-size: 76px; line-height: 1; color: #f5f2f8;
}
.cc-title::after {
  content: ""; display: block; width: 130px; height: 5px;
  background: #e3bd4a; border-radius: 11px; margin: 26px auto 0;
}
.cc-signs {
  display: flex; align-items: center; justify-content: center; gap: 49px; margin-top: 43px;
}
.cc-sign { display: flex; flex-direction: column; align-items: center; gap: 19px; }
.cc-sign img {
  width: 248px; height: 248px; border-radius: 50%; display: block; object-fit: cover;
  border: 8px solid rgba(227, 189, 74, 0.38);
  box-shadow: 0 0 0 11px rgba(227, 189, 74, 0.08);
}
.cc-sign b {
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-weight: 700; font-size: 42px;
}
.cc-amp {
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-size: 60px; color: #e3bd4a; padding-bottom: 54px;
}

.cc-rings { display: grid; grid-template-columns: repeat(5, 1fr); gap: 22px; width: 100%; align-items: start; }
.cc-ring { display: flex; flex-direction: column; align-items: center; gap: 17px; }
.cc-ring svg { width: 100%; max-width: 189px; height: auto; display: block; }
.cc-ring-lbl {
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 30px; color: rgba(245, 242, 248, 0.55); font-weight: 500; letter-spacing: 0.02em;
}
.cc-ring--main .cc-ring-lbl { color: #e3bd4a; font-weight: 700; }
.cc-track { stroke: rgba(255, 255, 255, 0.13); }
.cc-fill { stroke: #e3bd4a; stroke-linecap: round; transform: rotate(-90deg); transform-origin: 50% 50%; }
.cc-pct { fill: #f5f2f8; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; font-weight: 700; text-anchor: middle; font-variant-numeric: tabular-nums; }

.cc-verdict {
  display: grid; grid-template-columns: 1fr 1fr; gap: 54px; width: 100%;
  padding-top: 54px; border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.cc-col h4 {
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 32px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; margin: 0 0 28px;
}
.cc-col--pos h4 { color: #8fdcb6; }
.cc-col--neg h4 { color: #f0a6ba; }
.cc-col ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 28px; }
.cc-col li {
  position: relative; padding-left: 50px;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 32px; line-height: 1.28; color: rgba(245, 242, 248, 0.82);
}
.cc-col li::before { position: absolute; left: 0; top: -2px; font-weight: 700; font-size: 39px; }
.cc-col--pos li::before { content: "+"; color: #8fdcb6; }
.cc-col--neg li::before { content: "\\2013"; color: #f0a6ba; }

.cc-advice {
  width: 100%; border-radius: 38px; padding: 41px 45px;
  background: rgba(227, 189, 74, 0.12); border: 1px solid rgba(227, 189, 74, 0.38);
}
.cc-advice-k {
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 28px; font-weight: 700; letter-spacing: 0.18em;
  text-transform: uppercase; color: #e3bd4a; margin: 0 0 13px;
}
.cc-advice p {
  margin: 0; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 35px; line-height: 1.42; color: rgba(245, 242, 248, 0.82);
}

.cc-foot { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.cc-brand {
  display: flex; align-items: center; gap: 14px; white-space: nowrap;
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif; font-weight: 700;
  font-size: 39px; letter-spacing: 0.34em; line-height: 1;
  text-transform: uppercase; color: #f5f2f8;
}
.cc-brand .cc-spark { width: 26px; height: 26px; flex-shrink: 0; color: #e3bd4a; }
.cc-cta {
  white-space: nowrap;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 30px; color: rgba(245, 242, 248, 0.38); letter-spacing: 0.06em;
}
`;

export default CompatibilityCard;
